import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Container, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import swal from 'sweetalert';
import CommissionsListResults from '../../components/Comissoes/CommissionsListResults';
import CommissionsListToolbar from '../../components/Comissoes/CommissionsListToolbar';
import affiliateService from '../../services/affiliateService';
import { COMMISSION_STATUS } from '../../utils/commissionStatus';

const CommissionsList = () => {
  const auth = useSelector((state) => state.auth);
  const [isLoadingRegs, setIsLoadingRegs] = useState(true);
  const [regs, setRegs] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selected, setSelected] = useState([]);

  const get = async () => {
    setIsLoadingRegs(true);
    setRegs([]);

    try {
      const params = {
        page,
        per_page: perPage
      };

      if (status) {
        params.status = status;
      }

      if (dateFrom) {
        params.date_from = dateFrom;
      }

      if (dateTo) {
        params.date_to = dateTo;
      }

      const response = await affiliateService.getCommissions(auth.token, params);

      if (!response || !response.data) {
        throw new Error('Erro ao buscar comissões');
      }

      setRegs(response.data.data || []);
      setPage(response.data.page || 1);
      setPerPage(response.data.perPage || 25);
      setTotal(response.data.total || 0);
    } catch (e) {
      console.error(e);
      swal('Erro', 'Erro ao buscar comissões', 'error');
    } finally {
      setIsLoadingRegs(false);
    }
  };

  useEffect(() => {
    get();
    setSelected([]);
  }, [page, perPage, status, dateFrom, dateTo]);

  const requireAdmin = () => {
    if (!auth.user.is_admin) {
      swal('Erro', 'Apenas administradores podem alterar comissões', 'error');
      return false;
    }
    return true;
  };

  const runSingleTransition = async (serviceCall, successMessage, errorMessage) => {
    if (!requireAdmin()) return;
    try {
      const response = await serviceCall();
      if (response && response.data) {
        swal('Sucesso', successMessage, 'success');
        get();
      } else {
        throw new Error(errorMessage);
      }
    } catch (e) {
      console.error(e);
      const apiMessage = e?.response?.data?.error || errorMessage;
      swal('Erro', apiMessage, 'error');
    }
  };

  const handleConfirmPayment = (id) =>
    runSingleTransition(
      () => affiliateService.confirmPaymentCommission(id, auth.token),
      'Pagamento confirmado — comissão movida para "A Liberar"',
      'Erro ao confirmar pagamento'
    );

  const handleRelease = (id) =>
    runSingleTransition(
      () => affiliateService.releaseCommission(id, auth.token),
      'Comissão liberada para pagamento',
      'Erro ao liberar comissão'
    );

  const handlePay = (id) =>
    runSingleTransition(
      () => affiliateService.payCommission(id, auth.token),
      'Comissão marcada como paga',
      'Erro ao marcar comissão como paga'
    );

  const handleCancel = async (id) => {
    if (!requireAdmin()) return;

    const confirmCancel = await swal({
      title: 'Cancelar comissão?',
      text: 'Essa ação não pode ser desfeita.',
      icon: 'warning',
      buttons: ['Voltar', 'Cancelar comissão'],
      dangerMode: true
    });

    if (!confirmCancel) return;

    await runSingleTransition(
      () => affiliateService.cancelCommission(id, auth.token),
      'Comissão cancelada',
      'Erro ao cancelar comissão'
    );
  };

  const handleBulkRelease = async () => {
    if (!requireAdmin()) return;

    if (selected.length === 0) {
      swal('Atenção', 'Selecione ao menos uma comissão "A Liberar"', 'warning');
      return;
    }

    const confirm = await swal({
      title: 'Tem certeza?',
      text: `Liberar ${selected.length} comissão(ões) para pagamento?`,
      icon: 'warning',
      buttons: ['Cancelar', 'Liberar'],
      dangerMode: false
    });

    if (!confirm) return;

    try {
      const response = await affiliateService.bulkReleaseCommissions(selected, auth.token);
      if (response && response.data) {
        swal('Sucesso', response.data.message || 'Comissões liberadas!', 'success');
        setSelected([]);
        get();
      } else {
        throw new Error('Erro ao liberar comissões');
      }
    } catch (e) {
      console.error(e);
      swal('Erro', 'Erro ao liberar comissões em massa', 'error');
    }
  };

  const handleBulkPay = async () => {
    if (!requireAdmin()) return;

    if (selected.length === 0) {
      swal('Atenção', 'Selecione ao menos uma comissão "Liberado"', 'warning');
      return;
    }

    const confirm = await swal({
      title: 'Tem certeza?',
      text: `Marcar ${selected.length} comissão(ões) como pagas?`,
      icon: 'warning',
      buttons: ['Cancelar', 'Marcar como pagas'],
      dangerMode: false
    });

    if (!confirm) return;

    try {
      const response = await affiliateService.bulkPayCommissions(selected, auth.token);
      if (response && response.data) {
        swal('Sucesso', response.data.message || 'Comissões pagas!', 'success');
        setSelected([]);
        get();
      } else {
        throw new Error('Erro ao pagar comissões');
      }
    } catch (e) {
      console.error(e);
      swal('Erro', 'Erro ao pagar comissões em massa', 'error');
    }
  };

  // A seleção em massa só faz sentido para um status de cada vez.
  // O frontend mostra checkboxes apenas em linhas "pending_release" ou "released",
  // mas alinha a ação conforme o filtro atual ou o status da primeira selecionada.
  let bulkMode = null;
  if (status === COMMISSION_STATUS.PENDING_RELEASE) {
    bulkMode = 'release';
  } else if (status === COMMISSION_STATUS.RELEASED) {
    bulkMode = 'pay';
  }

  return (
    <>
      <Helmet>
        <title>Comissões | Sistema de Afiliados</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth={false}>
          <CommissionsListToolbar
            status={status}
            setStatus={setStatus}
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
            onRefresh={get}
            onBulkRelease={handleBulkRelease}
            onBulkPay={handleBulkPay}
            bulkMode={bulkMode}
            selectedCount={selected.length}
            isAdmin={auth.user.is_admin}
          />
          {isLoadingRegs && (
            <Box sx={{ pt: 3 }}>
              <Typography align="center" variant="h5" color="primary">
                <CircularProgress color="primary" sx={{ mb: 3 }} />
                <br />
                Buscando informações, por favor aguarde...
              </Typography>
            </Box>
          )}
          {!isLoadingRegs && (!regs || regs.length === 0) && (
            <Box sx={{ pt: 3 }}>
              <Typography align="center" variant="h5" color="primary">
                Nenhuma comissão localizada
              </Typography>
            </Box>
          )}
          {!isLoadingRegs && regs && regs.length > 0 && (
            <Box sx={{ pt: 3 }}>
              <CommissionsListResults
                regs={regs}
                page={page}
                setPage={setPage}
                perPage={perPage}
                setPerPage={setPerPage}
                total={total}
                onConfirmPayment={handleConfirmPayment}
                onRelease={handleRelease}
                onPay={handlePay}
                onCancel={handleCancel}
                isAdmin={auth.user.is_admin}
                selected={selected}
                setSelected={setSelected}
                bulkMode={bulkMode}
              />
            </Box>
          )}
        </Container>
      </Box>
    </>
  );
};

export default CommissionsList;
